'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { STATUS_LABELS, PartnerStatus } from '@/types';

interface CodeRow {
  code: string;
  country: string;
  note: string | null;
  partner_id: string;
  partner: string;
  status: string;
  total: number;
  lastUse: string | null;
  byMonth: number[];
}

interface CodesData {
  country: string;
  lastSync: { synced_at: string; source: string; rows: number; first_month: string; last_month: string } | null;
  months: string[];
  rows: CodeRow[];
  totalOrders: number;
  unused: number;
}

interface PartnerOpt {
  id: string;
  partner: string;
  country: string | null;
  status: string;
}

const CODE_COUNTRIES = ['Perú', 'Colombia', 'México'];
const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-');
  return `${MES[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

function syncAgo(iso: string): string {
  const t = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  const d = Math.floor((Date.now() - t) / 86400000);
  if (d <= 0) return 'hoy';
  if (d === 1) return 'ayer';
  return `hace ${d} días`;
}

// Normaliza variantes de país en la base (Peru/PE/CO) al país del código.
const normCountry = (c: string | null) =>
  !c ? '' : /^pe/i.test(c) ? 'Perú' : /^co/i.test(c) ? 'Colombia' : /^m[eé]x/i.test(c) ? 'México' : c;

const selectClass =
  'text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-300 cursor-pointer focus:outline-none focus:border-[#0890F1]';

export default function CodesPanel() {
  const [country, setCountry] = useState('Perú');
  const [data, setData] = useState<CodesData | null>(null);
  const [error, setError] = useState('');
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [form, setForm] = useState({ partner_id: '', code: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/codes?country=' + encodeURIComponent(country));
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [country]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetch('/api/partners')
      .then((r) => r.json())
      .then((ps: PartnerOpt[]) => setPartners(ps))
      .catch(() => {});
  }, []);

  // Alianzas del país, las vivas primero.
  const partnerOpts = useMemo(() => {
    const rank: Record<string, number> = { signed: 0, negotiating: 1, meeting_set: 2, intro_sent: 3 };
    return partners
      .filter((p) => normCountry(p.country) === country)
      .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || a.partner.localeCompare(b.partner));
  }, [partners, country]);

  const sorted = useMemo(
    () => (data ? [...data.rows].sort((a, b) => b.total - a.total || a.partner.localeCompare(b.partner)) : []),
    [data],
  );
  const maxCell = Math.max(1, ...sorted.flatMap((r) => r.byMonth));

  const add = async () => {
    if (!form.partner_id || !form.code.trim()) return;
    setSaving(true);
    await fetch('/api/codes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, country }),
    });
    setForm({ partner_id: form.partner_id, code: '', note: '' });
    setSaving(false);
    load();
  };

  const remove = async (code: string) => {
    if (!confirm(`¿Quitar ${code} de las alianzas? (no borra datos de uso)`)) return;
    await fetch(`/api/codes?code=${encodeURIComponent(code)}&country=${encodeURIComponent(country)}`, {
      method: 'DELETE',
    });
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={country} onChange={(e) => setCountry(e.target.value)} className={selectClass}>
          {CODE_COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {data?.lastSync ? (
          <span className="text-xs text-gray-500">
            Última sync: <b className="text-gray-700">{syncAgo(data.lastSync.synced_at)}</b> · datos{' '}
            {monthLabel(data.lastSync.first_month)} → {monthLabel(data.lastSync.last_month)}
          </span>
        ) : (
          <span className="text-xs text-amber-600">Sin datos sincronizados para {country} todavía.</span>
        )}
        <span
          className="ml-auto text-xs text-gray-400"
          title="Claude exporta 1 CSV del dashboard Sales Global Performance › Monthly order count_promotion (filtro país) y corre scripts/sync-promos.cjs"
        >
          🔄 Para actualizar: pídele a Claude “sincroniza códigos”
        </span>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <p className="text-xs font-medium text-gray-400 mb-1">Códigos de alianzas</p>
              <p className="text-2xl font-extrabold leading-none">{data.rows.length}</p>
              <p className="text-xs text-gray-500 mt-1.5">rastreados en {country}</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <p className="text-xs font-medium text-gray-400 mb-1">Canjes (histórico)</p>
              <p className="text-2xl font-extrabold leading-none text-[#22C55E]">{data.totalOrders}</p>
              <p className="text-xs text-gray-500 mt-1.5">pedidos con código de alianza</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
              <p className="text-xs font-medium text-gray-400 mb-1">Sin ningún canje</p>
              <p className="text-2xl font-extrabold leading-none text-red-500">{data.unused}</p>
              <p className="text-xs text-gray-500 mt-1.5">código entregado que nadie usó</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mb-4 overflow-x-auto">
            <div className="flex items-baseline gap-2 mb-3">
              <h2 className="text-sm font-bold text-gray-700">Uso por mes</h2>
              <span className="text-xs text-gray-400">pedidos por código · últimos 12 meses</span>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-[11px] text-gray-400">
                  <th className="text-left font-medium py-1.5 pr-3">Alianza</th>
                  <th className="text-left font-medium py-1.5 pr-3">Código</th>
                  {data.months.map((m) => (
                    <th key={m} className="font-medium py-1.5 px-1 text-center w-10">
                      {monthLabel(m)}
                    </th>
                  ))}
                  <th className="font-medium py-1.5 pl-3 text-right">Total</th>
                  <th className="font-medium py-1.5 pl-3 text-right">Último uso</th>
                  <th className="w-6" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => {
                  const st = STATUS_LABELS[r.status as PartnerStatus];
                  return (
                    <tr key={r.code} className="border-t border-gray-50 group">
                      <td className="py-1.5 pr-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: st?.color }} title={st?.label} />
                          <span className="text-gray-800">{r.partner}</span>
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">
                        <span className="font-mono text-xs font-semibold text-[#0890F1]" title={r.note ?? ''}>
                          {r.code}
                        </span>
                        {r.note && <span className="text-[11px] text-gray-400 ml-2">{r.note}</span>}
                      </td>
                      {r.byMonth.map((n, i) => (
                        <td key={i} className="px-0.5 py-1">
                          <div
                            className="h-6 rounded text-[11px] flex items-center justify-center font-semibold"
                            style={
                              n
                                ? {
                                    backgroundColor: `rgba(34,197,94,${0.18 + 0.7 * (n / maxCell)})`,
                                    color: n / maxCell > 0.5 ? '#fff' : '#166534',
                                  }
                                : { backgroundColor: '#F3F4F6', color: '#D1D5DB' }
                            }
                          >
                            {n || '·'}
                          </div>
                        </td>
                      ))}
                      <td className={`py-1.5 pl-3 text-right font-bold ${r.total ? 'text-gray-800' : 'text-red-500'}`}>
                        {r.total}
                      </td>
                      <td className="py-1.5 pl-3 text-right text-xs text-gray-500 whitespace-nowrap">
                        {r.lastUse ? monthLabel(r.lastUse) : <span className="text-red-400">nunca</span>}
                      </td>
                      <td className="pl-2">
                        <button
                          onClick={() => remove(r.code)}
                          className="text-gray-300 hover:text-red-500 text-xs opacity-0 group-hover:opacity-100"
                          title="Quitar código"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={data.months.length + 5} className="py-4 text-sm text-gray-400">
                      Todavía no hay códigos de alianzas en {country}. Agrega uno abajo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="text-[11px] text-gray-400 mt-3">
              Fuente: Looker Studio “Sales Global Performance” › Monthly order count_promotion. Un código que no aparece
              en el export = 0 pedidos. Códigos únicos por socio (ej. 1000 de Club El Comercio) no se ven aquí uno por
              uno.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-3">➕ Agregar código a una alianza</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={form.partner_id}
                onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
                className={selectClass + ' max-w-[260px]'}
              >
                <option value="">Alianza…</option>
                {partnerOpts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.partner} · {STATUS_LABELS[p.status as PartnerStatus]?.label ?? p.status}
                  </option>
                ))}
              </select>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="CÓDIGO"
                className="text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 font-mono w-40 focus:outline-none focus:border-[#0890F1]"
              />
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="nota (ej. S/60 primer pedido)"
                className="text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 flex-1 min-w-[180px] focus:outline-none focus:border-[#0890F1]"
                onKeyDown={(e) => e.key === 'Enter' && add()}
              />
              <button
                onClick={add}
                disabled={saving || !form.partner_id || !form.code.trim()}
                className="text-sm px-3 py-1.5 rounded-lg bg-[#0890F1] text-white font-semibold disabled:opacity-40"
              >
                Agregar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
